import React, { useState, useMemo } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import FinalCTA from "@/components/FinalCTA";
import BrochureCTA from "@/components/BrochureCTA";
import { BLOGS, BLOG_CATEGORIES } from "@/data/content";
import { buildItemListSchema } from "@/utils/schema";
import Img from "@/components/Img";
import { slotKeyFor } from "@/data/imageSlots";

export default function Blogs() {
  const [cat, setCat] = useState("All");
  const list = useMemo(() => cat === "All" ? BLOGS : BLOGS.filter((b) => b.category === cat), [cat]);
  const featured = list[0];
  const rest = list.slice(1);
  const itemList = buildItemListSchema(
    BLOGS.map((b) => ({ name: b.title, description: b.excerpt, url: `/blogs/${b.slug}` })),
    "/blogs"
  );

  return (
    <div data-testid="page-blogs" className="bg-[#FAF6F1]">

      <SEO
        title="Insights & Planning Guides — Petals & Promises Studio Notes"
        description="Long-form planning guides on luxury weddings, destination weddings, corporate events, decor directions, venue checklists, and devotional gatherings — written by our senior planners."
        path="/blogs"
        keywords="wedding planning blog, event planning guide, destination wedding tips, corporate event planning, devotional event guide, mata ki chowki guide, jagran planning"
        jsonLd={itemList}
      />
<section className="pt-28 md:pt-32 pb-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="pill">Insights</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[84px] leading-[1.0] mt-6 text-[#3B312D] max-w-4xl">
            Notes from the <span className="italic text-[#B65980]">studio</span>.
          </h1>
          <p className="mt-7 max-w-2xl text-[16.5px] text-[#3B312D]/80 leading-relaxed">
            Long-form planning guides, design directions, and quiet wisdom from years of designing weddings,
            corporate experiences, and devotional gatherings.
          </p>
        </div>
      </section>

      <section className="py-6 bg-[#F4DCD8]/35 border-y border-[rgba(59,49,45,0.06)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex gap-2 overflow-x-auto no-scrollbar">
          {["All", ...BLOG_CATEGORIES].map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-[12px] tracking-[0.14em] uppercase border transition font-semibold ${
                cat === c ? "bg-[#3B312D] text-white border-[#3B312D]" : "bg-white text-[#3B312D] border-[rgba(59,49,45,0.12)] hover:border-[#B65980]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {featured && (
        <section className="py-14 md:py-20">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <Link to={`/blogs/${featured.slug}`} className="grid grid-cols-1 md:grid-cols-12 gap-8 lux-card overflow-hidden" data-testid={`blog-featured-${featured.slug}`}>
              <div className="md:col-span-7 image-zoom h-72 md:h-[440px] relative overflow-hidden">
                <Img imageKey={slotKeyFor.blogCover(featured)} defaultSrc={featured.image} defaultAlt={featured.title} className="w-full h-full" />
              </div>
              <div className="md:col-span-5 p-8 md:p-10 flex flex-col justify-center">
                <p className="text-[11px] tracking-[0.24em] uppercase text-[#8A9A7B] font-semibold">{featured.category} · {featured.read}</p>
                <h2 className="font-display text-3xl md:text-5xl mt-4 text-[#3B312D] leading-tight">{featured.title}</h2>
                <p className="mt-5 text-[15px] text-[#3B312D]/80 leading-relaxed">{featured.excerpt}</p>
                <span className="link-gold mt-7 text-[12px] tracking-[0.18em] uppercase text-[#B65980] inline-flex font-semibold">Read article <ArrowUpRight size={14}/></span>
              </div>
            </Link>
          </div>
        </section>
      )}

      <section className="pb-20 md:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((b, i) => (
            <Reveal key={b.slug} delay={i * 70} className="h-full">
              <Link to={`/blogs/${b.slug}`} className="lux-card overflow-hidden flex flex-col h-full" data-testid={`blog-card-${b.slug}`}>
                <div className="relative h-56 image-zoom shrink-0"><Img imageKey={slotKeyFor.blogCover(b)} defaultSrc={b.image} defaultAlt={b.title} className="w-full h-full" /></div>
                <div className="p-7 flex flex-col flex-1">
                  <p className="text-[11px] tracking-[0.24em] uppercase text-[#8A9A7B] font-semibold">{b.category} · {b.read}</p>
                  <h3 className="font-display text-2xl mt-3 text-[#3B312D] leading-snug">{b.title}</h3>
                  <p className="mt-3 text-[14px] text-[#3B312D]/80 leading-relaxed flex-1">{b.excerpt}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <BrochureCTA />
      <FinalCTA />
    </div>
  );
}
